import { type RelatedStackItem } from "legacyComponents/LineItemContainer.types";
import moment from "moment";

import type { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";
import type { InvoiceLineItemMetadata } from "@btnet/services-btnet/handlers/models/invoiceLineItemMetadata";
import type { LineItemGroupStrategy } from "@btnet/services-btnet/handlers/models/lineItemGroupStrategy";

import { type ICostCodeType } from "utilities/lineItem/lineItemHelper";

import { CustomFieldPrintItem } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import { type DiscussionPrintAPI } from "commonComponents/entity/discussion/DiscussionPrintContainer/DiscussionPrintContainer.types";
import { LineItemPrintEntity } from "commonComponents/entity/lineItem/LineItemPrintContainer/LineItemPrintContainer.types";
import { PrintHeaderAPI } from "commonComponents/print/PrintHeader/PrintHeader.types";

import {
    type CustomerInvoicePaymentStatus,
    type OwnerInvoiceLineItemColumn,
} from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.api.types";
import { type CertificationRole } from "entity/progressInvoices/common/approvalStatus.type";
import { type TaxMethod } from "entity/tax/common/tax.types";
import {
    type ISubtotalBreakdown,
    type TaxRateBreakdownInfo,
} from "entity/tax/common/TaxRateBreakdown/TaxRateBreakdown.api.types";

import { type default as printAPIResponseExample } from "./OwnerInvoicePrint.api.json";

export interface IInvoiceLineItemPrintData {
    invoiceLineItemMetadata: InvoiceLineItemMetadata;
    billLineItemId?: number | null;
    relatedAccountingCostId?: number | null;
    relatedStackLinks?: Record<number, RelatedStackItem[]> | null;
    title?: string | null;
    retainageWithheld?: number;
    storedMaterials?: number;
}

export class InvoiceLineItemPrintEntity extends LineItemPrintEntity {
    constructor(data: IInvoiceLineItemPrintData, key: number) {
        super(data, key);
        this.invoiceLineItemMetadata = data.invoiceLineItemMetadata;
        this.relatedBillLineItemId = data.billLineItemId ?? null;
        this.relatedAccountingCostId = data.relatedAccountingCostId ?? null;
        this.relatedStackLinks = data.relatedStackLinks ?? null;
        this.itemTitle = data.title ?? null;
        this.retainageWithheld = data.retainageWithheld;
        this.storedMaterials = data.storedMaterials;
    }
    invoiceLineItemMetadata?: InvoiceLineItemMetadata;
    relatedBillLineItemId?: number | null;
    relatedAccountingCostId?: number | null;
    relatedStackLinks?: Record<number, RelatedStackItem[]> | null;
    itemTitle?: string | null;
    retainageWithheld?: number;
    storedMaterials?: number;
}

// APIx will return lowercase properties, while existing API returns uppercase. Need to handle both cases
interface ICostCodePairing {
    item1?: number;
    item2?: string;
    Item1?: number;
    Item2?: string;
}

// AiaWorkflowStepType serializes as its integer value (0 = Contractor, 1 = Architect, 2 = Client)
// on both the print-entity and the v3 approval-workflow responses. Only the two signing roles certify.
export const mapActiveSignatureRole = (value: unknown): CertificationRole | undefined => {
    switch (value) {
        case 0:
            return "contractor";
        case 1:
            return "architect";
        default:
            return undefined;
    }
};

export interface ICompletedCertificationSignature {
    role: CertificationRole;
    signerName?: string | null;
    signedDate?: moment.Moment | null;
    signatureImageBase64?: string | null;
}

interface ICompletedSignatureData {
    role?: number | null;
    signerName?: string | null;
    signedDate?: string | Date | null;
    signatureImageBase64?: string | null;
}

export const mapCompletedSignatures = (
    values: ICompletedSignatureData[] | undefined | null,
): ICompletedCertificationSignature[] => {
    const result: ICompletedCertificationSignature[] = [];
    for (const v of values ?? []) {
        const role = mapActiveSignatureRole(v.role);
        if (!role) {
            continue;
        }
        result.push({
            role,
            signerName: v.signerName,
            signedDate: v.signedDate ? moment(v.signedDate) : null,
            signatureImageBase64: v.signatureImageBase64,
        });
    }
    return result;
};

export class OwnerInvoicePrintEntity {
    constructor(
        data: Omit<typeof printAPIResponseExample, "costCodeIdTitlePairings"> & {
            costCodeIdTitlePairings: ICostCodePairing[];
            costCategoryOptions?: { id: number; name: string }[];
            costCodeCategoryMap?: Record<number, number>;
            activeSignatureRole?: number | null;
            completedSignatures?: ICompletedSignatureData[] | null;
            hideCostCodes?: boolean;
            subtotalBreakdown?: ISubtotalBreakdown | null;
        },
    ) {
        this.hasViewableLineItems = data.hasViewableLineItems;
        this.LineItems = data.invoiceLineItems.map(
            (I: any, key: number) =>
                new InvoiceLineItemPrintEntity(I as IInvoiceLineItemPrintData, key),
        );
        this.hasCustomFields = data.hasCustomFields;
        this.customFields = data.customFieldsPrint.map((l) => new CustomFieldPrintItem(l));
        this.customerInvoiceId = data.customerInvoiceId;
        this.Status = data.status;
        this.DueDate = moment(data.dueDate);
        this.releasedDate = moment(data.releasedDate);
        this.invoiceDate = moment(data.invoiceDate);
        this.Balance = data.balance;
        this.paymentSum = data.paymentSum;
        this.Description = data.description;
        this.invoicePayments = data.invoicePayments.map((p) => new OnlinePaymentPrintEntity(p));
        this.invoice = data.invoice;
        this.showPaymentDateCell = data.showPaymentDateCell;
        this.PaidDate = moment(data.paidDate);
        this.allowCustomFields = data.allowCustomFields;
        this.jobsite = new Jobsite(data);
        this.builderId = data.builderId;
        this.hasPermissionToViewLineItems = data.hasPermissionToViewLineItems;
        this.showMerchantPayments = data.showMerchantPayments;
        this.printHeader = new PrintHeaderAPI(data.printHeader);
        this.showLineItems = data.showLineItems;
        this.ownerInvoiceLineItemColumnPreferences =
            data.ownerInvoiceLineItemColumnPreferences ?? [];
        this.canShowPaymentFromPrintoutInformation = data.canShowPaymentFromPrintoutInformation;
        this.showPaymentFromPrintoutInformation = data.showPaymentFromPrintoutInformation;
        this.qrCodeUrl = data.qrCodeUrl;
        this.paymentCode = data.paymentCode;
        this.taxMethod = data.taxMethod;
        this.showFees = data.showFees;
        this.groupLineItems = data.groupLineItems;
        this.hideCostCodes = data.hideCostCodes ?? false;
        this.invoiceFormat = data.invoiceFormat as InvoiceFormat;
        const categoryTitleById = new Map<number, string>(
            (data.costCategoryOptions ?? []).map(
                (category) => [category.id, category.name] as [number, string],
            ),
        );
        const costCodeCategoryMap: Record<number, number> = data.costCodeCategoryMap ?? {};
        this.costCodeDict = new Map(
            data.costCodeIdTitlePairings.map((x) => {
                const id = (x.Item1 ?? x.item1)!;
                const categoryId = costCodeCategoryMap[id];
                return [
                    id,
                    {
                        id,
                        name: x.Item2 ?? x.item2,
                        categoryTitle: categoryTitleById.get(categoryId),
                        extraData: {
                            displayInactiveTag: false,
                            flatRateCode: false,
                            isActive: true,
                            costCategoryId: categoryId,
                        },
                    } as ICostCodeType,
                ];
            }),
        );
        this.lineItemGroupStrategy = data.lineItemGroupStrategy as LineItemGroupStrategy;
        this.originalContractSum = data.originalContractSum ?? 0;
        this.netChangeOrderTotal = data.netChangeOrderTotal ?? 0;
        this.showCostCodes = data.showCostCodes ?? true;
        this.showCategories = data.showCategories ?? true;
        this.showContractorCertification = data.showContractorCertification ?? true;
        this.showArchitectCertification = data.showArchitectCertification ?? true;
        this.showRetainage = data.showRetainage ?? true;
        this.showStoredMaterials = data.showStoredMaterials ?? true;
        this.showItems = data.showItems ?? true;
        this.activeSignatureRole = mapActiveSignatureRole(data.activeSignatureRole);
        this.completedSignatures = mapCompletedSignatures(data.completedSignatures);
        this.hideLaborCostAndMarkup = data.hideLaborCostAndMarkup ?? false;
        this.subtotalBreakdown = data.subtotalBreakdown ?? undefined;
    }

    hasPermissionToViewLineItems: boolean;
    hasViewableLineItems: boolean;
    canShowPaymentFromPrintoutInformation: boolean;
    LineItems: InvoiceLineItemPrintEntity[];
    showLineItems: boolean;
    ownerInvoiceLineItemColumnPreferences: OwnerInvoiceLineItemColumn[];
    hasCustomFields: boolean;
    customFields: CustomFieldPrintItem[];
    customerInvoiceId: string;
    Status: string;
    DueDate: moment.Moment;
    releasedDate: moment.Moment;
    invoiceDate: moment.Moment;
    Balance: string;
    paymentSum: number;
    Description: string;
    invoicePayments: OnlinePaymentPrintEntity[];
    invoice: PrintInvoice;
    showPaymentDateCell: boolean;
    PaidDate: moment.Moment;
    allowCustomFields: boolean;
    jobsite: Jobsite;
    builderId: number;
    showMerchantPayments: boolean;
    printHeader: PrintHeaderAPI;
    showPaymentFromPrintoutInformation: boolean;
    qrCodeUrl: string | null;
    paymentCode: string | null;
    taxMethod: TaxMethod;
    showFees: boolean;
    groupLineItems: boolean;
    hideCostCodes: boolean;
    lineItemGroupStrategy?: LineItemGroupStrategy;
    invoiceFormat: InvoiceFormat;
    costCodeDict: Map<number, ICostCodeType>;
    originalContractSum: number;
    netChangeOrderTotal: number;
    showCostCodes: boolean;
    showCategories: boolean;
    showContractorCertification: boolean;
    showArchitectCertification: boolean;
    showRetainage: boolean;
    showStoredMaterials: boolean;
    showItems: boolean;
    activeSignatureRole?: CertificationRole;
    completedSignatures: ICompletedCertificationSignature[];
    hideLaborCostAndMarkup: boolean;
    /** Builder cost and markup making up the subtotal; absent when the breakdown should not render. */
    subtotalBreakdown?: ISubtotalBreakdown;
}

class Jobsite {
    constructor(data: any) {
        this.jobAddress = data.jobsite.jobAddress;
        this.jobName = data.jobsite.jobName;
        this.ownerAddress = data.jobsite.ownerAddress;
        this.ownerContact = data.jobsite.ownerContact;
    }
    jobAddress: string;
    jobName: string;
    ownerAddress: string;
    ownerContact: string;
}

export class PrintInvoice {
    id: number;
    title: string;
    amount: number;
    amountPaid: number;
    paidDate: string;
    description: string;
    closingText: string;
    builderID: number;
    jobID: number;
    isDetailMode: boolean;
    taxRateBreakdown?: TaxRateBreakdownInfo;
    totalPrice: number;
    taxMethod: TaxMethod | null;
}

export class OnlinePaymentPrintEntity {
    constructor(data: any) {
        this.paymentDate = moment(data.paymentDate);
        this.paymentAmount = data.paymentAmount;
        this.paymentMethod = data.paymentMethod;
        this.paymentStatus = data.paymentStatus;
        this.refundReason = data.refundReason;
        this.feeAmount = data.feeAmount;
        this.totalAmount = data.paymentAmount + data.feeAmount;
        this.isFooter = data.isFooter ?? false;
    }

    paymentDate: moment.Moment;
    paymentAmount: number;
    paymentMethod: string;
    paymentStatus: CustomerInvoicePaymentStatus;
    refundReason: string | null;
    feeAmount: number;
    totalAmount: number;
    isFooter: boolean;
}

export class OwnerInvoiceDetailPrint {
    constructor(data: any) {
        this.title = data.title;
        if (data.dateEntered !== null && data.dateEntered !== undefined) {
            this.dateEntered = moment(data.dateEntered);
        } else {
            this.dateEntered = data.dateEntered;
        }
        this.jobName = data.jobName;
        this.jobID = data.jobID;
        this.description = data.description;
        this.cost = data.cost;
        this.customFields = data.customFields.map((d: any) => new CustomFieldPrintItem(d));
    }

    title: string;
    dateEntered: moment.Moment;
    jobName: string;
    jobID: number;
    description: string;
    cost: number;

    discussions: DiscussionPrintAPI[];
    customFields: CustomFieldPrintItem[];
}

export class OwnerInvoiceDetailPrintOptions {
    constructor(data: any) {
        this.printLineItems = data.printLineItems;
        this.printCustomFields = data.printCustomFields;
        this.printPaymentCodeInfo = data.showPaymentFromPrintoutInformation;
    }

    printLineItems: boolean;
    printCustomFields: boolean;
    printPaymentCodeInfo: boolean;
}
